// QuickStudyDock — dock flutuante no rodapé direito do desktop.
//
// Estilo InboxDock/PeerChatDock. Toggle minimizado ↔ expandido. Quando
// expandido, mostra um mini-shell com lista de conversas (mais curta) +
// chat + composer compacto. Aluno pode minimizar a qualquer momento;
// estado sobrevive enquanto a aba estiver aberta.

import { useEffect } from "preact/hooks";
import {
  ChevronDown,
  ExternalLink,
  MessageCirclePlus,
  Minimize2,
  Sparkles,
  X
} from "lucide-react";
import {
  loadQuickStudyConversations,
  loadQuickStudyLimits,
  newQuickStudyConversation,
  openQuickStudyConversation,
  quickStudyActiveId,
  quickStudyConversations,
  quickStudyDockOpen,
  quickStudyLimits,
  setQuickStudyDockOpen
} from "./quickStudyStore";
import { QuickStudyChat, QuickStudyComposer } from "./QuickStudy";

export function QuickStudyDock() {
  useEffect(() => {
    void loadQuickStudyLimits();
  }, []);

  const limits = quickStudyLimits.value;
  if (!limits.enabled) return null;

  const open = quickStudyDockOpen.value;
  const activeId = quickStudyActiveId.value;
  const convs = quickStudyConversations.value;

  function toggle() {
    setQuickStudyDockOpen(!open);
    if (!open) {
      void loadQuickStudyConversations();
    }
  }

  function popOut() {
    const url = activeId
      ? `?view=quickstudy-window&convId=${encodeURIComponent(activeId)}`
      : "?view=quickstudy-window";
    window.open(
      url,
      `quickstudy-${activeId ?? "new"}`,
      "width=560,height=820,resizable=yes,scrollbars=yes"
    );
    setQuickStudyDockOpen(false);
  }

  // Posição evita overlap com:
  //   - PersistentPlayer (music) em `bottom-4 right-4` (~56px área)
  //   - PeerChatDock em `bottom-0 right-20` (faixa horizontal de chats)
  //   - InboxDock em `bottom-4 left-4` (outro canto, sem conflito)
  // Solução: QuickStudy fica UMA LINHA ACIMA do player → bottom-[5.5rem]
  // (~88px). Painel expandido cresce pra cima a partir dessa âncora.
  //
  // Mobile: botão flutuante mostra só o ícone (sem label) pra ocupar menos
  // espaço; quando expande, painel cobre quase toda a tela.
  // Morpho: por padrão fica icon-only (discreto). No hover, o label
  // "Tira-dúvida" expande pra direita com transição suave (~150ms).
  // Padrão consistente com outros morpho buttons da plataforma
  // [[feedback-morpho-buttons]].
  if (!open) {
    return (
      <button
        type="button"
        onClick={toggle}
        title="Tira-dúvida rápido"
        aria-label="Abrir tira-dúvida rápido"
        class="group fixed bottom-[5.5rem] right-4 z-40 inline-flex items-center rounded-full bg-violet-600 p-2.5 text-xs font-bold text-white shadow-lg transition-all duration-200 hover:bg-violet-700 hover:px-3"
      >
        <Sparkles className="size-4 shrink-0" />
        <span class="ml-0 max-w-0 overflow-hidden whitespace-nowrap transition-all duration-200 group-hover:ml-1.5 group-hover:max-w-[160px]">
          Tira-dúvida
        </span>
      </button>
    );
  }

  return (
    <div class="fixed bottom-[5.5rem] right-4 z-40 flex h-[min(600px,calc(100vh-7.5rem))] w-[min(380px,calc(100vw-2rem))] flex-col rounded-lg border border-border bg-card shadow-2xl">
      <header class="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div class="flex min-w-0 items-center gap-2">
          <Sparkles className="size-4 shrink-0 text-violet-600" />
          <span class="truncate text-sm font-bold">Tira-dúvida</span>
        </div>
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={popOut}
            title="Abrir em janela separada"
            aria-label="Pop-out"
            class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ExternalLink className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={toggle}
            title="Minimizar"
            aria-label="Minimizar"
            class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Minimize2 className="size-3.5" />
          </button>
        </div>
      </header>

      {/* Linha de conversas compacta */}
      <div class="flex items-center gap-1 overflow-x-auto border-b border-border bg-background/50 px-2 py-1.5">
        <button
          type="button"
          onClick={() => void newQuickStudyConversation()}
          title="Nova conversa"
          class="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
        >
          <MessageCirclePlus className="size-3" />
          Nova
        </button>
        {convs.slice(0, 8).map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => void openQuickStudyConversation(c.id)}
            title={c.title}
            class={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors ${
              c.id === activeId
                ? "border-violet-500 bg-violet-500/10 text-violet-700 dark:text-violet-300"
                : "border-border bg-background hover:bg-muted"
            }`}
          >
            {c.title.length > 16 ? `${c.title.slice(0, 16)}…` : c.title}
          </button>
        ))}
      </div>

      <QuickStudyChat compact />

      <div class="border-t border-border p-2">
        <QuickStudyComposer
          conversationId={activeId}
          compact
          autoCreate
        />
      </div>
    </div>
  );
}
